import { TWENTY_BASE_URL } from './config';
import { CATEGORY_OPTIONS, STAGE_OPTIONS, type Option } from './categories';

// The category/stage chips are driven by the LIVE Twenty field definitions, so
// adding/renaming/removing an option in the platform flows through to the add-in
// without a rebuild. The hardcoded lists in categories.ts are the FALLBACK only:
// if the metadata API is unreachable (network, permissions), we use them so
// logging never breaks. Read from the metadata API (/metadata), not /graphql.

export type FieldOptions = {
  companyCategory: Option[];
  personCategory: Option[];
  companyStage: Option[];
};

export const FALLBACK_FIELD_OPTIONS: FieldOptions = {
  companyCategory: CATEGORY_OPTIONS,
  personCategory: CATEGORY_OPTIONS,
  companyStage: STAGE_OPTIONS,
};

type RawOption = { value: string; label: string; position: number };

const metadataQuery = async <TData>(
  token: string,
  query: string,
  variables: Record<string, unknown>,
): Promise<TData> => {
  const response = await fetch(`${TWENTY_BASE_URL}/metadata`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ query, variables }),
  });
  const payload = (await response.json()) as {
    data?: TData;
    errors?: { message: string }[];
  };
  if (payload.errors?.length) throw new Error(payload.errors[0].message);
  if (!payload.data) throw new Error('Empty metadata response');
  return payload.data;
};

// Sort by the option's position (the order shown in the platform) and drop the
// metadata-only fields, leaving the {value,label} shape the pickers expect.
const toOptions = (raw: RawOption[] | null): Option[] =>
  (raw ?? [])
    .slice()
    .sort((a, b) => a.position - b.position)
    .map(({ value, label }) => ({ value, label }));

// Loaded once per session: field options rarely change within a session, and a
// re-fetch on every message switch would be wasteful. A failed load is NOT
// cached, so a transient error retries on the next call.
let cache: FieldOptions | null = null;

export const loadFieldOptions = async (token: string): Promise<FieldOptions> => {
  if (cache) return cache;
  try {
    // 1) Resolve the company/person object ids (filters can't match by name).
    const objData = await metadataQuery<{
      objects: { edges: { node: { id: string; nameSingular: string } }[] };
    }>(
      token,
      `query { objects(paging: { first: 200 }) { edges { node { id nameSingular } } } }`,
      {},
    );
    const byName = new Map(
      objData.objects.edges.map((e) => [e.node.nameSingular, e.node.id]),
    );
    const ids = [byName.get('company'), byName.get('person')].filter(
      (id): id is string => !!id,
    );
    if (!ids.length) return FALLBACK_FIELD_OPTIONS;

    // 2) Pull those two objects' fields and pick category + lifecycleStage.
    const fieldData = await metadataQuery<{
      fields: {
        edges: {
          node: {
            name: string;
            object: { nameSingular: string };
            options: RawOption[] | null;
          };
        }[];
      };
    }>(
      token,
      `query ($ids: [UUID!]) {
        fields(filter: { objectMetadataId: { in: $ids } }, paging: { first: 300 }) {
          edges { node { name object { nameSingular } options } }
        }
      }`,
      { ids },
    );
    const optionsFor = (object: string, name: string): RawOption[] | null =>
      fieldData.fields.edges
        .map((e) => e.node)
        .find((n) => n.object.nameSingular === object && n.name === name)
        ?.options ?? null;

    const companyCategory = toOptions(optionsFor('company', 'category'));
    const personCategory = toOptions(optionsFor('person', 'category'));
    const companyStage = toOptions(optionsFor('company', 'lifecycleStage'));

    // Per-field fallback: if a field returned no options (renamed/removed field),
    // keep the built-in list for that one rather than rendering an empty picker.
    cache = {
      companyCategory: companyCategory.length
        ? companyCategory
        : FALLBACK_FIELD_OPTIONS.companyCategory,
      personCategory: personCategory.length
        ? personCategory
        : FALLBACK_FIELD_OPTIONS.personCategory,
      companyStage: companyStage.length
        ? companyStage
        : FALLBACK_FIELD_OPTIONS.companyStage,
    };
    return cache;
  } catch (error) {
    // Never block logging on a metadata failure — fall back to the built-in
    // lists and warn (visible in the host's dev tools) for diagnosis.
    console.warn(
      '[CLS add-in] Could not load field options from the platform; using built-in defaults.',
      error,
    );
    return FALLBACK_FIELD_OPTIONS;
  }
};
